import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { messagesApi, type ApiCall, type Message, type ProcessPendingResult } from '~/lib/api';
import { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import { useMessageStream } from '~/hooks/useMessageStream';
import { useAuth } from '~/contexts/AuthContext';

type Tab = 'messages' | 'api-calls' | 'errors';

export default function DebugPage() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>('messages');
  const [lastResult, setLastResult] = useState<ProcessPendingResult | null>(null);

  // SSE stream for real-time messages
  const {
    messages: streamMessages,
    clearMessages,
  } = useMessageStream({ enabled: true });

  // Fetch initial messages on mount
  const { data: initialData, isLoading } = useQuery({
    queryKey: ['messages', 'initial'],
    queryFn: () => messagesApi.list(100),
    staleTime: 0, // Refetch on each mount so navigating back shows latest
  });

  // Fetch stats (poll since it's aggregate data)
  const { data: statsData } = useQuery({
    queryKey: ['messages', 'stats'],
    queryFn: messagesApi.stats,
    refetchInterval: 10000, // Stats refresh every 10 seconds
  });

  // Fetch pending message counts (admin only)
  const { data: pendingData } = useQuery({
    queryKey: ['messages', 'pending-count'],
    queryFn: messagesApi.pendingCount,
    refetchInterval: activeTab === 'errors' ? 15000 : 30000,
    enabled: isAdmin,
  });

  // Fetch error/stuck messages (admin only, when errors tab active)
  const { data: errorMessagesData, isLoading: errorsLoading } = useQuery({
    queryKey: ['messages', 'errors'],
    queryFn: () => messagesApi.errorMessages(100),
    refetchInterval: activeTab === 'errors' ? 15000 : false,
    enabled: isAdmin && activeTab === 'errors',
  });

  // Reprocess stuck messages mutation
  const reprocessMutation = useMutation({
    mutationFn: (limit: number) => messagesApi.processPending(limit),
    onSuccess: (data) => {
      setLastResult(data);
      queryClient.invalidateQueries({ queryKey: ['messages', 'pending-count'] });
      queryClient.invalidateQueries({ queryKey: ['messages', 'errors'] });
      queryClient.invalidateQueries({ queryKey: ['messages', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['api-calls'] });
    },
  });

  // Fetch API calls
  const { data: apiCallsData, refetch: refetchApiCalls } = useQuery({
    queryKey: ['api-calls'],
    queryFn: () => messagesApi.apiCalls(50),
    refetchInterval: activeTab === 'api-calls' ? 5000 : false,
  });

  // Merge initial messages with stream messages (stream takes priority)
  const [messages, setMessages] = useState<typeof streamMessages>([]);

  useEffect(() => {
    const initial = initialData?.messages || [];
    // Combine: stream messages first (newest), then initial that aren't in stream
    const streamIds = new Set(streamMessages.map((m) => m.id));
    const combined = [
      ...streamMessages,
      ...initial.filter((m) => !streamIds.has(m.id)),
    ];
    // Sort by timestamp desc and limit to 100
    combined.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    setMessages(combined.slice(0, 100));
  }, [streamMessages, initialData]);

  const stats = statsData;

  return (
    <div className="flex flex-col gap-6 h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Debug View</h1>
          <p className="text-text-secondary">
            Monitor incoming messages and system status
          </p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={clearMessages}
            className="btn btn-secondary text-sm"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Total Messages"
          value={stats?.total_messages ?? '-'}
        />
        <StatCard
          label="Last Hour"
          value={stats?.messages_last_hour ?? '-'}
        />
        <StatCard
          label="Unique Authors"
          value={stats?.unique_authors ?? '-'}
        />
        <StatCard
          label="Last Message"
          value={stats?.last_message_at ? formatTimeAgo(stats.last_message_at) : 'Never'}
        />
      </div>

      {/* Tabs */}
      <div className="card flex-1 flex flex-col min-h-0">
        <div className="flex items-center border-b border-border shrink-0">
          <button
            onClick={() => setActiveTab('messages')}
            className={clsx(
              'px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === 'messages'
                ? 'border-accent text-accent'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            )}
          >
            Messages ({messages.length})
          </button>
          <button
            onClick={() => { setActiveTab('api-calls'); refetchApiCalls(); }}
            className={clsx(
              'px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === 'api-calls'
                ? 'border-accent text-accent'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            )}
          >
            API Calls ({apiCallsData?.api_calls?.length || 0})
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab('errors')}
              className={clsx(
                'px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === 'errors'
                  ? 'border-loss text-loss'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              )}
            >
              Errors
              {pendingData && pendingData.total_unprocessed > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs bg-loss/20 text-loss">
                  {pendingData.total_unprocessed}
                </span>
              )}
            </button>
          )}
        </div>

        {activeTab === 'messages' && (
          <>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
              </div>
            ) : messages.length === 0 ? (
              <div className="p-8 text-center text-text-secondary">
                No messages received yet. Make sure:
                <ul className="mt-2 text-sm space-y-1">
                  <li>• Chrome extension is installed and enabled</li>
                  <li>• API URL is configured in extension popup</li>
                  <li>• At least one channel is subscribed</li>
                  <li>• Discord tab is open for subscribed channel</li>
                </ul>
              </div>
            ) : (
              <div className="divide-y divide-border overflow-y-auto flex-1">
                {messages.map((msg) => (
                  <MessageRow key={msg.id} message={msg} />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'api-calls' && (
          <>
            {!apiCallsData?.api_calls?.length ? (
              <div className="p-8 text-center text-text-secondary">
                No API calls yet. API calls are made when messages are processed for trading signals.
              </div>
            ) : (
              <div className="divide-y divide-border overflow-y-auto flex-1">
                {apiCallsData.api_calls.map((call) => (
                  <ApiCallRow key={call.id} call={call} />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'errors' && isAdmin && (
          <>
            {/* Status summary + reprocess button */}
            <div className="p-4 border-b border-border shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-xs">
                  {pendingData && (
                    <>
                      {pendingData.pending > 0 && (
                        <span className="px-2 py-1 rounded bg-warning/10 text-warning">
                          {pendingData.pending} pending
                        </span>
                      )}
                      {pendingData.queued > 0 && (
                        <span className="px-2 py-1 rounded bg-warning/10 text-warning">
                          {pendingData.queued} queued
                        </span>
                      )}
                      {pendingData.rate_limited > 0 && (
                        <span className="px-2 py-1 rounded bg-orange-500/10 text-orange-400">
                          {pendingData.rate_limited} rate limited
                        </span>
                      )}
                      {pendingData.error > 0 && (
                        <span className="px-2 py-1 rounded bg-loss/10 text-loss">
                          {pendingData.error} error
                        </span>
                      )}
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {lastResult && (
                    <span className="text-xs text-text-secondary">
                      {lastResult.processed} processed, {lastResult.signals_created} signals
                    </span>
                  )}
                  <button
                    onClick={() => reprocessMutation.mutate(20)}
                    disabled={reprocessMutation.isPending}
                    className={clsx(
                      'px-3 py-1.5 rounded text-xs font-medium transition-colors',
                      reprocessMutation.isPending
                        ? 'bg-surface-3 text-text-muted cursor-not-allowed'
                        : 'bg-accent text-surface-0 hover:bg-accent-dim'
                    )}
                  >
                    {reprocessMutation.isPending ? 'Processing...' : 'Reprocess'}
                  </button>
                </div>
              </div>
              {reprocessMutation.isError && (
                <div className="mt-2 text-xs text-loss">
                  Failed: {reprocessMutation.error instanceof Error ? reprocessMutation.error.message : 'Unknown error'}
                </div>
              )}
            </div>
            {/* Error message list */}
            {errorsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-loss"></div>
              </div>
            ) : !errorMessagesData?.messages?.length ? (
              <div className="p-8 text-center text-text-secondary">
                No stuck messages. All messages have been processed successfully.
              </div>
            ) : (
              <div className="divide-y divide-border overflow-y-auto flex-1">
                {errorMessagesData.messages.map((msg) => (
                  <ErrorMessageRow key={msg.id} message={msg} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: string | number;
  color?: 'green' | 'red' | 'yellow';
}) {
  return (
    <div className="card p-4">
      <div className="text-[11px] font-mono uppercase tracking-wider text-text-muted">{label}</div>
      <div
        className={clsx(
          'text-2xl font-mono font-bold mt-1',
          color === 'green' && 'text-profit',
          color === 'red' && 'text-loss',
          color === 'yellow' && 'text-warning',
          !color && 'text-text-primary'
        )}
      >
        {value}
      </div>
    </div>
  );
}

const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  processed: { bg: 'bg-profit/20', text: 'text-profit' },
  error: { bg: 'bg-loss/20', text: 'text-loss' },
  pending: { bg: 'bg-warning/20', text: 'text-warning' },
  queued: { bg: 'bg-blue-500/20', text: 'text-blue-400' },
  rate_limited: { bg: 'bg-orange-500/20', text: 'text-orange-400' },
};

function MessageRow({ message }: { message: Message }) {
  const [expanded, setExpanded] = useState(false);
  const status = message.processing_status || 'unknown';
  const style = STATUS_STYLES[status] || { bg: 'bg-surface-3', text: 'text-text-secondary' };

  return (
    <div
      className="p-4 hover:bg-surface-2/50 cursor-pointer transition-colors"
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm">
            <span className={clsx('px-2 py-0.5 rounded text-xs font-medium', style.bg, style.text)}>
              {status}
            </span>
            <span className="font-medium text-blue-400">
              {message.author_username || 'Unknown'}
            </span>
            <span className="text-text-muted">in</span>
            <span className="text-purple-400">
              #{message.channel_name || 'unknown'}
            </span>
            <span className="text-text-muted">•</span>
            <span className="text-text-muted">
              {formatTimeAgo(message.timestamp)}
            </span>
          </div>
          <div className="mt-1 text-text-secondary break-words">
            {message.content.length > 200 && !expanded
              ? message.content.substring(0, 200) + '...'
              : message.content}
          </div>
        </div>
        <div className="text-xs text-text-muted whitespace-nowrap">
          ID: {message.id}
        </div>
      </div>

      {expanded && (
        <div className="mt-3 p-3 bg-surface-3 rounded text-xs font-mono text-text-secondary overflow-x-auto">
          <pre>{JSON.stringify(JSON.parse(message.raw_payload || '{}'), null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

function ErrorMessageRow({ message }: { message: Message }) {
  const [expanded, setExpanded] = useState(false);
  const status = message.processing_status || 'unknown';
  const style = STATUS_STYLES[status] || { bg: 'bg-surface-3', text: 'text-text-secondary' };

  return (
    <div
      className="p-4 hover:bg-surface-2/50 cursor-pointer transition-colors"
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <span className={clsx('px-2 py-0.5 rounded text-xs font-medium', style.bg, style.text)}>
              {status}
            </span>
            <span className="font-medium text-blue-400">
              {message.author_username || 'Unknown'}
            </span>
            <span className="text-text-muted">in</span>
            <span className="text-purple-400">
              #{message.channel_name || 'unknown'}
            </span>
            <span className="text-text-muted">
              {formatTimeAgo(message.timestamp)}
            </span>
          </div>
          <div className="mt-1 text-text-secondary break-words">
            {message.content.length > 200 && !expanded
              ? message.content.substring(0, 200) + '...'
              : message.content}
          </div>
        </div>
        <div className="text-xs text-text-muted whitespace-nowrap">
          ID: {message.id}
        </div>
      </div>

      {expanded && (
        <div className="mt-3 p-3 bg-surface-3 rounded text-xs font-mono text-text-secondary overflow-x-auto">
          <pre>{JSON.stringify(JSON.parse(message.raw_payload || '{}'), null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

/** End-to-end pipeline latency: message ingest → parse logged (dispatch + LLM). */
function pipelineLatencyMs(call: ApiCall): number | null {
  if (!call.message_created_at) return null;
  const toMs = (s: string) => new Date(s.replace(' ', 'T') + (s.endsWith('Z') ? '' : 'Z')).getTime();
  const ms = toMs(call.created_at) - toMs(call.message_created_at);
  return Number.isFinite(ms) ? Math.max(0, ms) : null;
}

function ApiCallRow({ call }: { call: ApiCall }) {
  const [expanded, setExpanded] = useState(false);
  const latencyMs = pipelineLatencyMs(call);

  return (
    <div
      className="p-4 hover:bg-surface-2/50 cursor-pointer transition-colors"
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <span className={clsx(
              'px-2 py-0.5 rounded text-xs font-medium',
              call.error ? 'bg-loss/20 text-loss' : 'bg-profit/20 text-profit'
            )}>
              {call.error ? 'Error' : 'Success'}
            </span>
            <span className="text-text-secondary">{call.model}</span>
            {latencyMs !== null && (
              <span
                className={clsx(
                  'px-1.5 py-0.5 rounded text-xs font-medium',
                  latencyMs < 4000 ? 'bg-profit/20 text-profit' : 'bg-warning/20 text-warning'
                )}
                title="End-to-end: message arrival → parsed (dispatch + LLM)"
              >
                ⚡ {(latencyMs / 1000).toFixed(1)}s
              </span>
            )}
            {call.duration_ms && (
              <span className="text-text-muted" title="LLM call duration">{call.duration_ms}ms LLM</span>
            )}
            {call.input_tokens && call.output_tokens && (
              <span className="text-text-muted">
                {call.input_tokens} in / {call.output_tokens} out
              </span>
            )}
            <span className="text-text-muted">•</span>
            <span className="text-text-muted">{formatTimeAgo(call.created_at)}</span>
          </div>
          {call.author_username && (
            <div className="mt-1 text-sm">
              <span className="text-blue-400">{call.author_username}</span>
              <span className="text-text-muted">: </span>
              <span className="text-text-secondary">
                {call.message_content?.substring(0, 100)}
                {(call.message_content?.length || 0) > 100 ? '...' : ''}
              </span>
            </div>
          )}
          {call.error && (
            <div className="mt-1 text-sm text-loss">
              {call.error.substring(0, 150)}
              {call.error.length > 150 ? '...' : ''}
            </div>
          )}
        </div>
        <div className="text-xs text-text-muted whitespace-nowrap">
          ID: {call.id}
        </div>
      </div>

      {expanded && (
        <div className="mt-3 space-y-3">
          <div>
            <div className="text-xs font-medium text-text-muted mb-1">Prompt:</div>
            <div className="p-3 bg-surface-3 rounded text-xs font-mono text-text-secondary overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap">
              {call.prompt}
            </div>
          </div>
          {call.response && (
            <div>
              <div className="text-xs font-medium text-text-muted mb-1">Response:</div>
              <div className="p-3 bg-surface-3 rounded text-xs font-mono text-text-secondary overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap">
                {call.response}
              </div>
            </div>
          )}
          {call.error && (
            <div>
              <div className="text-xs font-medium text-text-muted mb-1">Error:</div>
              <div className="p-3 bg-loss/10 rounded text-xs font-mono text-loss overflow-x-auto">
                {call.error}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatTimeAgo(timestamp: string): string {
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffSecs < 10) return 'just now';
  if (diffSecs < 60) return `${diffSecs}s ago`;
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}
